import axios from 'axios';
import { getAuthDetails } from './helper';
import { toastEvents } from './toastEvents';
import Cookies from 'universal-cookie';

// Automatically detect development mode and set API URL
const getApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:4000';
  }
  // For production, use the environment variable or fallback
  return process.env.REACT_APP_API_URL || 'http://localhost:4000';
};

const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Crucial for CSRF cookies
});

// Request interceptor to add JWT token in headers if available
api.interceptors.request.use(
  (config) => {
    const userDetails = getAuthDetails();
    const token = userDetails ? userDetails.accessToken : '';
    const cookies = new Cookies(null, { path: '/' });
    const csrfToken = cookies.get('XSRF-TOKEN');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    // Show success toast for mutating operations (POST, PUT, PATCH, DELETE)
    const method = response.config.method?.toLowerCase();
    const isMutating = ['post', 'put', 'patch', 'delete'].includes(method || '');

    if (isMutating && response.status >= 200 && response.status < 300) {
      const message = response.data?.message || getDefaultSuccessMessage(method);
      if (response.data?.message || method !== 'get') {
        toastEvents.success(message);
      }
    }

    return response;
  },
  (error) => {
    const { response } = error;

    // Handle 401 Unauthorized
    if (response && response.status === 401) {
      // Don't redirect if we're already on a public page
      const publicPaths = ['/', '/login', '/forgot-password', '/reset-password', '/token-expiry'];
      if (!publicPaths.includes(window.location.pathname)) {
        window.location.href = '/token-expiry';
      }
      return Promise.reject(error);
    }

    // Show error toast for failed requests (unless it's a 401 which handles its own redirect)
    if (response) {
      const message = response.data?.message || getDefaultErrorMessage(response.status);
      toastEvents.error(message);
    } else {
      toastEvents.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  },
);

// Helper functions for default messages
function getDefaultSuccessMessage(method?: string): string {
  switch (method) {
    case 'post': return 'Created successfully';
    case 'put':
    case 'patch': return 'Updated successfully';
    case 'delete': return 'Deleted successfully';
    default: return 'Operation completed successfully';
  }
}

function getDefaultErrorMessage(status?: number): string {
  switch (status) {
    case 400: return 'Bad request. Please check your input.';
    case 403: return 'Access denied. You do not have permission.';
    case 404: return 'Resource not found.';
    case 409: return 'Conflict. Resource may already exist.';
    case 422: return 'Validation failed. Please check your input.';
    case 429: return 'Too many requests. Please try again later.';
    case 500: return 'Server error. Please try again later.';
    case 503: return 'Service unavailable. Please try again later.';
    default: return 'An error occurred. Please try again.';
  }
}

export default api;
